import { Typography, Button, Form, message, Spin, Space } from 'antd';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useTimer } from 'react-timer-hook';
import useHttpClient from '../../../shared/sendRequest/useHttpClient';
import EndPoints from '../../../shared/endPoints/EndPoints.json';
import { colors } from '../../../shared/styles/baseStyle';
import AuthLayout from '../../../components/AuthLayout/AuthLayout';
import OtpInput from '../../../components/OtpInput/OtpInput';
import useAuthFlowRouteGuard from '../../../shared/hooks/useAuthFlowRouteGuard';
import { useAuthFlowContext } from '../../../shared/context/auth-flow-context';
import {
  button,
  expireText,
  remainingTimeColor,
  remainingTimeText,
  resendButton,
  space
} from './styles';
import AuthCard from '../../../components/AuthCard/AuthCard';

const { Text } = Typography;

export default function VerifyEmail() {
  const history = useHistory();
  const [form] = Form.useForm();
  const { tempCode } = useAuthFlowContext();
  const { sendRequest } = useHttpClient();
  const shouldRender = useAuthFlowRouteGuard(!!tempCode);

  const { seconds, minutes, isRunning, restart } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {}
  });

  const isExpired = !isRunning;

  const handleOtpSuccess = data => {
    if (!data?.expiresAt) return;
    message.success('Ο κωδικός μιας χρήσης έχει αποσταλεί στο email σου');
    const trimmed = data.expiresAt.replace(/(\.\d{3})\d*/, '$1');
    const fixed = trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`;
    const expiryTimestamp = new Date(fixed);
    restart(expiryTimestamp);
  };

  const {
    data: requestOtpData,
    refetch,
    isFetching
  } = useQuery(
    ['getOtp', tempCode],
    async () =>
      sendRequest({
        endpoint: EndPoints.OTP.REQUESTOTP,
        method: 'POST',
        bodyData: { tempCode }
      }),
    {
      enabled: !!tempCode,
      retry: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        handleOtpSuccess(data);
      },
      onError: error => {
        if (error.data?.TempCode) {
          history.replace('/');
        }
      }
    }
  );

  const { mutate, isLoading } = useMutation(sendRequest, {
    onSuccess: () => {
      history.replace('/set-password');
    },
    onSettled: () => {
      form.resetFields();
    }
  });

  if (!shouldRender) return null;

  const onFinish = values => {
    mutate({
      endpoint: EndPoints.OTP.VERIFYEMAIL,
      method: 'POST',
      bodyData: { tempCode, otp: Number(values.otp) }
    });
  };

  const handleResend = () => {
    form.resetFields();
    refetch();
  };

  const mmss = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0'
  )}`;

  return (
    <AuthLayout>
      <Spin spinning={isFetching || isLoading} size="large">
        <AuthCard
          title="Επιβεβαίωση Email"
          subtitle="Εισήγαγε τον κωδικό μιας χρήσης που έχει αποσταλεί στο email σου"
        >
          <Form form={form} onFinish={onFinish}>
            <Form.Item name="otp" style={{ marginBottom: 28 }}>
              <OtpInput length={6} />
            </Form.Item>
            <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
              {() => {
                const otp = form.getFieldValue('otp') || '';
                const disabled = otp.length !== 6 || isExpired;
                return (
                  <Button
                    htmlType="submit"
                    block
                    size="large"
                    disabled={disabled}
                    style={{
                      ...button,
                      background: disabled
                        ? colors.disabledGrey
                        : colors.anytimeBlue
                    }}
                  >
                    Επιβεβαίωση
                  </Button>
                );
              }}
            </Form.Item>
          </Form>
          {requestOtpData && (
            <>
              {!isExpired && (
                <Text type="secondary" style={remainingTimeText}>
                  Ο κωδικός ισχύει για τα επόμενα{' '}
                  <Text strong style={remainingTimeColor}>
                    {mmss}
                  </Text>{' '}
                  λεπτά
                </Text>
              )}
              <Space size={3} style={{ ...space, marginTop: 0 }}>
                <Text
                  type="secondary"
                  style={{
                    ...expireText,
                    ...(isExpired && { fontWeight: 'bold' })
                  }}
                >
                  {isExpired ? 'Ο κωδικός έληξε.' : 'Δεν έλαβες κωδικό;'}
                </Text>
                <Button type="link" onClick={handleResend} style={resendButton}>
                  Επαναποστολή
                </Button>
              </Space>
            </>
          )}
        </AuthCard>
      </Spin>
    </AuthLayout>
  );
}
